import { ArrowUpRight, Timer, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency, formatCurrencyAdaptive, formatNumber } from '@/lib/utils';
import { formatFechaHoraTurno, formatTiempoTranscurrido } from '@/features/turnos/lib/turnos-format';

import type { ActiveShift } from '../dashboard.api';

interface ActiveShiftsPanelProps {
  shifts: ActiveShift[] | undefined;
  loading: boolean;
}

/**
 * Resumen de turnos abiertos.
 *
 * Es el resumen ligero (`/api/dashboard_active_shifts`); el detalle completo del
 * monitoreo vive en `/turnos-en-vivo`.
 */
/** Cuántos turnos se muestran directamente en la tarjeta. */
const PREVIEW_LIMIT = 4;

/** Fila de un turno abierto. */
function ShiftRow({ turno }: { turno: ActiveShift }) {
  const horas = Number(turno.horas_transcurridas || 0);
  const vencido = horas >= 24;

  return (
    <li
      className={cn(
        'flex items-center justify-between gap-3 rounded-control border px-3.5 py-2.5',
        vencido ? 'border-amber-200 bg-amber-50/60' : 'border-ink-100',
      )}
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink-800">{turno.operador}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-ink-400">
          <Timer className="size-3" aria-hidden="true" />
          {formatFechaHoraTurno(turno.hora_llegada)}
          <span className="text-ink-300">·</span>
          {formatTiempoTranscurrido(Math.round(horas * 60))}
        </p>
      </div>

      <div className="shrink-0 text-right">
        <p
          title={formatCurrency(turno.monto_acumulado)}
          className="font-mono text-sm font-semibold text-ink-900 tabular-nums"
        >
          {formatCurrencyAdaptive(turno.monto_acumulado, 100_000)}
        </p>
        <p className="text-[11px] text-ink-400">{formatNumber(turno.transacciones)} ops</p>
      </div>
    </li>
  );
}

export function ActiveShiftsPanel({ shifts, loading }: ActiveShiftsPanelProps) {
  const [abierto, setAbierto] = useState(false);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-control" />
        ))}
      </div>
    );
  }

  const lista = shifts ?? [];

  if (lista.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-ink-200 px-4 py-8 text-center">
        <Users className="mx-auto size-5 text-ink-300" aria-hidden="true" />
        <p className="mt-2 text-sm font-medium text-ink-700">No hay turnos abiertos</p>
        <p className="mt-0.5 text-xs text-ink-500">Los turnos aparecerán aquí en cuanto se abran.</p>
      </div>
    );
  }

  const total = lista.reduce((sum, turno) => sum + Number(turno.monto_acumulado || 0), 0);
  const transacciones = lista.reduce((sum, turno) => sum + Number(turno.transacciones || 0), 0);
  const vistaPrevia = lista.slice(0, PREVIEW_LIMIT);

  return (
    <div className="flex h-full flex-col gap-3">
      <ul className="space-y-2">
        {vistaPrevia.map((turno) => (
          <ShiftRow key={turno.id} turno={turno} />
        ))}
      </ul>

      {lista.length > PREVIEW_LIMIT ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAbierto(true)}
          className="self-start"
        >
          Ver todos ({lista.length})
        </Button>
      ) : null}

      <div className="mt-auto space-y-3 border-t border-ink-100 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="brand">{formatNumber(lista.length)} abiertos</Badge>
            {vencidosCount(lista) > 0 ? (
              <Badge variant="warning">{formatNumber(vencidosCount(lista))} con más de 24 h</Badge>
            ) : null}
          </div>

          <p className="text-xs text-ink-500">
            Acumulado:{' '}
            <strong title={formatCurrency(total)} className="font-semibold text-ink-800">
              {formatCurrencyAdaptive(total)}
            </strong>{' '}
            · {formatNumber(transacciones)} transacciones
          </p>
        </div>

        <Link
          to="/turnos-en-vivo"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 transition-colors hover:text-brand-700"
        >
          Ver monitoreo en vivo
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>

      <Dialog
        open={abierto}
        onClose={() => setAbierto(false)}
        title="Turnos activos"
        description={`${lista.length} turnos abiertos en este momento`}
        size="lg"
      >
        <ul className="space-y-2">
          {lista.map((turno) => (
            <ShiftRow key={turno.id} turno={turno} />
          ))}
        </ul>
      </Dialog>
    </div>
  );
}

function vencidosCount(shifts: ActiveShift[]): number {
  return shifts.filter((turno) => Number(turno.horas_transcurridas || 0) >= 24).length;
}
